import React, { useState } from 'react';
import { Link, Outlet, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sun, CloudSun, Moon, Plus, ChevronRight } from 'lucide-react';
import { SwipeableList, SwipeableListItem, SwipeAction, TrailingActions } from 'react-swipeable-list';
import { deleteWorkout } from '../../features/workoutSlice';
import 'react-swipeable-list/dist/styles.css';
import { format } from 'date-fns';

import { auth } from '../../firebase';

import { Calendar } from '../ui/calendar';
import { ScrollArea } from '../ui/scroll-area';

import LoadingSpinner from '../custom/LoadingSpinner';

import { createWorkout, getWorkouts } from '../../features/workoutSlice';

type TimeOfDay = 'morning' | 'afternoon' | 'evening';

const getTimeOfDay = (date: Date): TimeOfDay => {
  const hour = date.getHours();
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

const TimeIcon = ({ time, className }: { time: TimeOfDay; className?: string }) => {
  switch (time) {
    case 'morning':
      return <Sun className={className} />;
    case 'afternoon':
      return <CloudSun className={className} />;
    case 'evening':
      return <Moon className={className} />;
  }
};

const getTimeIconColor = (time: TimeOfDay): string => {
  switch (time) {
    case 'morning':
      return 'text-tmw-accent';
    case 'afternoon':
      return 'text-tmw-warning';
    case 'evening':
      return 'text-blue-500';
  }
};

const getTimeIconBg = (time: TimeOfDay): string => {
  switch (time) {
    case 'morning':
      return 'bg-tmw-accent/10';
    case 'afternoon':
      return 'bg-amber-500/10';
    case 'evening':
      return 'bg-blue-500/10';
  }
};

const isValidDate = (date: Date): boolean => {
  return date instanceof Date && !isNaN(date.getTime());
};

const parseWorkoutDate = (createdAt: any): Date | null => {
  if (!createdAt) return null;
  const date = new Date(createdAt);
  return isValidDate(date) ? date : null;
};

const Workouts: React.FC = () => {
  const queryClient = useQueryClient();
  const user = auth.currentUser;
  const { workoutID } = useParams();

  const [selectedDay, setSelectedDay] = useState<Date | undefined>(new Date());

  const workoutQuery = useQuery({
    queryKey: ["workouts", user!.uid, "workout"],
    queryFn: () => getWorkouts(),
    retryDelay: 250,
    retry: 1,
  });

  if (workoutQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  const handleDayClick = (day: Date) => {
    setSelectedDay(day);
  };

  const handleCreateWorkout = () => {
    createWorkout();
    queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout'] });
  };

  const handleDeleteWorkout = async (workoutId: string) => {
    try {
      await deleteWorkout(workoutId);
      queryClient.invalidateQueries({ queryKey: ['workouts', user!.uid, 'workout'] });
    } catch (error) {
      console.error('Error deleting workout:', error);
    }
  };

  const workoutCountsByDay = workoutQuery.data?.reduce((acc: Record<string, number>, workout: any) => {
    const date = parseWorkoutDate(workout.createdAt);
    if (date) {
      const dateKey = format(date, 'yyyy-MM-dd');
      acc[dateKey] = (acc[dateKey] || 0) + 1;
    }
    return acc;
  }, {} as Record<string, number>);

  const trailingActions = (workoutID: string) => (
    <TrailingActions>
      <SwipeAction
        destructive={true}
        onClick={() => handleDeleteWorkout(workoutID)}
      >
        <span className="text-sm font-bold uppercase text-white pt-2 px-4">Delete</span>
      </SwipeAction>
    </TrailingActions>
  );

  if (workoutID) {
    return <Outlet />;
  }

  const filteredWorkouts = workoutQuery.data
    ?.filter((workout: any) => {
      const workoutDate = parseWorkoutDate(workout.createdAt);
      if (!workoutDate) return false; // Filter out workouts with invalid dates
      return selectedDay ? workoutDate.toDateString() === selectedDay.toDateString() : true;
    })
    .sort((a: any, b: any) => {
      const dateA = parseWorkoutDate(a.createdAt);
      const dateB = parseWorkoutDate(b.createdAt);
      return (dateB?.getTime() || 0) - (dateA?.getTime() || 0);
    });

  const selectedDateLabel = selectedDay
    ? format(selectedDay, 'MMMM d').toUpperCase() +
      (selectedDay.toDateString() === new Date().toDateString() ? ' — TODAY' : '')
    : 'ALL WORKOUTS';

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 flex-shrink-0">
        <h1 className="text-[28px] font-bold text-foreground">Workouts</h1>
        <button
          onClick={handleCreateWorkout}
          className="flex items-center justify-center w-9 h-9 rounded-full bg-tmw-accent"
        >
          <Plus className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* Calendar */}
      <div className="px-5 flex-shrink-0">
        <Calendar
          onDayClick={handleDayClick}
          selected={selectedDay}
          workoutCounts={workoutCountsByDay}
        />
      </div>

      {/* Divider */}
      <div className="h-px bg-border mx-0 my-4 flex-shrink-0" />

      {/* Workout List */}
      <div className="px-5 flex-1 flex flex-col min-h-0">
        <p className="text-sm font-semibold text-muted-foreground mb-3 flex-shrink-0">
          {selectedDateLabel}
        </p>

        {workoutQuery.isSuccess && filteredWorkouts && filteredWorkouts.length > 0 ? (
          <ScrollArea className="flex-1">
            <SwipeableList className="space-y-3 pb-28">
              {filteredWorkouts.map((workout: any) => {
                const workoutDate = parseWorkoutDate(workout.createdAt) || new Date();
                const timeOfDay = getTimeOfDay(workoutDate);
                const timeString = workoutDate.toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                  hour12: true,
                }).toUpperCase();

                return (
                  <SwipeableListItem
                    key={workout.id}
                    trailingActions={trailingActions(workout.id)}
                    className="w-full"
                  >
                    <Link to={`/workouts/${workout.id}`} className="block w-full">
                      <div className="bg-tmw-surface-elevated rounded-xl p-4 shadow-[0_2px_8px_rgba(0,0,0,0.06)]">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center justify-center w-9 h-9 rounded-lg ${getTimeIconBg(timeOfDay)}`}>
                              <TimeIcon time={timeOfDay} className={`h-[18px] w-[18px] ${getTimeIconColor(timeOfDay)}`} />
                            </div>
                            <div>
                              <p className="text-base font-semibold text-foreground">
                                Workout
                              </p>
                              <p className="text-[13px] text-muted-foreground">
                                {timeString} · {workout.exercises?.length || 0} exercises
                              </p>
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-muted-foreground" />
                        </div>

                        {workout.exercises && workout.exercises.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-3">
                            {Array.from(new Set<string>(workout.exercises.flatMap((ex: any) => ex.bodyParts || []))).slice(0, 3).map((part) => (
                              <span
                                key={part}
                                className="px-2.5 py-1 text-xs font-medium text-foreground bg-secondary rounded-md capitalize"
                              >
                                {part}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </Link>
                  </SwipeableListItem>
                );
              })}
            </SwipeableList>
          </ScrollArea>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No workouts for this day
          </div>
        )}
      </div>
    </div>
  );
};

export default Workouts;
